import axios from "axios";
import { baseURL, soapNotes, superTailsUrl } from "./../../util/server";

function headerConfig(contentType, auth, formData) {
  const header = {};

  if (contentType && typeof contentType === "string") {
    header["Content-Type"] = contentType;
  } else {
    header["Content-Type"] = "application/json";
  }

  if (formData === true) {
    header["Content-Type"] = "multipart/form-data";
  }

  if (auth === true) {
    const token = localStorage.getItem("accessToken");
    header.Authorization = `Bearer ${token}`;
  }

  return header;
}

// Create a plain axios instance for refresh token calls (without interceptors to avoid loops)
const refreshAxiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Function to setup token refresh interceptor
const setupTokenRefreshInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Skip interceptor for refresh token endpoints to avoid infinite loops
      const requestUrl =
        originalRequest.url ||
        originalRequest.baseURL + originalRequest.url ||
        "";
      const isRefreshEndpoint =
        requestUrl.includes("/refresh") || requestUrl.includes("/login");

      if (isRefreshEndpoint) {
        return Promise.reject(error);
      }

      // Check if error has response and status is 401, and request hasn't been retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Refresh token
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) {
            // No refresh token available, reject original error
            console.log(
              "No refresh token available, rejecting original 401 error"
            );
            return Promise.reject(error);
          }

          console.log("Attempting to refresh token...");

          // Try refresh endpoint - refresh endpoints typically don't require auth
          // Use relative path since refreshAxiosInstance already has baseURL
          const refreshEndpoint = "/user/refresh";
          const fullRefreshUrl = `${baseURL}${refreshEndpoint}`;
          console.log(`Calling refresh endpoint: ${fullRefreshUrl}`);

          // Try refresh without access token first (refresh endpoints usually don't need it)
          let refreshResponse;
          try {
            refreshResponse = await refreshAxiosInstance.post(refreshEndpoint, {
              refreshToken: refreshToken,
            });
          } catch (noAuthError) {
            // If that fails with 401, try with current access token
            if (noAuthError.response?.status === 401) {
              console.log(
                "Refresh without auth failed, trying with current access token..."
              );
              const currentAccessToken = localStorage.getItem("accessToken");
              refreshResponse = await axios.post(
                fullRefreshUrl,
                { refreshToken: refreshToken },
                {
                  headers: {
                    "Content-Type": "application/json",
                    ...(currentAccessToken && {
                      Authorization: `Bearer ${currentAccessToken}`,
                    }),
                  },
                }
              );
            } else {
              throw noAuthError;
            }
          }

          console.log("Token refresh response:", refreshResponse.data);

          // Update token
          const { accessToken, refreshToken: newRefreshToken } =
            refreshResponse.data || {};

          if (accessToken) {
            localStorage.setItem("accessToken", accessToken);
            // Update refresh token if provided
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken);
            }
            // Update the original request header
            originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
            // Retry original request with updated token
            console.log("Retrying original request with new token");
            return instance(originalRequest);
          } else {
            console.error("No accessToken in refresh response");
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          console.error("Refresh error details:", {
            message: refreshError.message,
            status: refreshError.response?.status,
            data: refreshError.response?.data,
            url: refreshError.config?.url,
          });

          // If refresh fails, reject the original error (not the refresh error)
          // This allows the app to handle the 401 normally
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default ({ contentType, auth, formData }) => {
  const instance = axios.create({
    baseURL,
    headers: headerConfig(contentType, auth, formData),
  });

  // Setup token refresh interceptor for this instance
  setupTokenRefreshInterceptor(instance);

  return instance;
};

export const chatgptApi = axios.create({
  baseURL: "https://api.openai.com",
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_GPT_KEY}`, // Add your API key here
    "Content-Type": "application/json",
  },
});

export const soapApi = axios.create({
  baseURL: soapNotes,
  headers: {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
  },
});

export const soapApiJson = axios.create({
  baseURL: soapNotes,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const superTailsApi = axios.create({
  baseURL: superTailsUrl,
  headers: {
    // Authorization: `Bearer ${api_key}`, // Add your API key here
    "Content-Type": "application/json",
  },
});
